import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type MetaFunction } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css'
import sortby from 'lodash.sortby'
import { VideoComponent } from '#app/components/video.tsx'
import { prisma } from '#app/utils/db.server.ts'

export const meta: MetaFunction = () => [
	{ title: 'Live in Studio Videos' },
	{
		name: 'description',
		content: `Searchable database of live studio musical performances.`,
	},
]

export async function loader() {
	const channels = await prisma.channel
		.findMany({
			include: {
				videos: {
					where: {
						NOT: {
							title: {
								contains: 'interview', // Just filter these from the front page entirely, they are visible everywhere else
							},
						},
					},
					take: 3,
					orderBy: {
						publishedAt: 'desc',
					},
					include: {
						artist: true,
					},
				},
			},
		})
		.then(results => {
			return sortby(
				results,
				result => result.videos.at(0)?.publishedAt,
				'desc',
			).reverse()
		})

	const totalVideoCount = await prisma.video.count()

	return {
		channels,
		totalVideoCount,
	}
}

export default function Index() {
	const { channels, totalVideoCount } = useLoaderData<typeof loader>()

	return (
		<>
			<div className="container mb-6">
				<h1 className="mb-4 text-center text-5xl">Live in Studio Videos</h1>
				<p className="text-center">
					Browse <span className="text-red-500">{totalVideoCount}</span> videos
					from channels like{' '}
					<Link to="#kexp" className="text-blue-500 underline">
						KEXP
					</Link>
					,{' '}
					<Link to="#NPRMusic" className="text-blue-500 underline">
						NPR Music
					</Link>
					,{' '}
					<Link to="#audiotree" className="text-blue-500 underline">
						Audiotree
					</Link>
					, and others.
				</p>
			</div>
			<main className="container mb-12 mt-2 flex max-w-screen-lg flex-col justify-center">
				<ul className="grid grid-flow-dense gap-12">
					{channels.map(channel => (
						<li key={channel.youtubeId} id={channel.handle}>
							<Link to={`/channel/${channel.title}`}>
								<h2 className="mb-6 inline-block text-h3 text-blue-500 underline">
									{channel.title}
								</h2>
							</Link>
							<ul className="grid grid-flow-row-dense gap-8 sm:grid-cols-2 lg:grid-cols-3">
								{channel.videos.map(({ artist, ...video }) => (
									<VideoComponent
										video={video}
										artist={artist}
										key={video.youtubeId}
									/>
								))}
							</ul>
						</li>
					))}
				</ul>
			</main>
		</>
	)
}

export const handle: SEOHandle = {
	getSitemapEntries: () => {
		return [
			{
				route: '/',
				priority: 1,
			},
		]
	},
}
